import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Facebook, Instagram, Image as ImageIcon, Video, FileText, Calendar,
  Send, Clock, Loader2, Upload, X, Images, Heart, MessageCircle, Send as SendIcon,
  Bookmark, MoreHorizontal, ThumbsUp, Share2, GripVertical, Trash2, Save, Pencil,
  Copy, ChevronLeft, ChevronRight, AlertCircle, MapPin, AtSign, Music, Info, Plus,
  Smile, RefreshCw, BadgeCheck,
} from "lucide-react";
import { PlatformCheck, Field, StatusPill, EmptyPreview, AvatarFallback } from "./publish/_atoms";
import { FbPreview, IgPreview, MusicNote, useMediaAspect, clampIgFeedAspect } from "./publish/previews";
import { SingleMediaField, MultiMediaUploader } from "./publish/uploaders";
import { ComposeToolbar } from "./publish/toolbar";
import { DraftsList, CalendarView } from "./publish/browsers";

/**
 * Unified publisher: FB + IG, single or both simultaneously.
 * • Multi-platform: pick FB, IG, or both — publish fires parallel API calls
 * • Media uploads with cancel + remove, live preview thumbs
 * • Carousel supports drag-reorder (HTML5 native)
 * • Sticky right-side live preview mimicking FB feed + IG feed post
 */
export default function Publish() {
  const { t } = useTranslation();
  const [platforms, setPlatforms] = useState({ facebook: true, instagram: false });
  const [tab, setTab] = useState("compose");
  const [history, setHistory] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [editingId, setEditingId] = useState(null);   // id of draft/scheduled being edited

  // Content
  const [text, setText] = useState("");             // shared text (message on FB, caption on IG)
  // Single media (image OR video) — array of {url, kind} for uniformity
  const [singleMedia, setSingleMedia] = useState(null); // {url, kind} | null
  // Carousel — up to 10
  const [carousel, setCarousel] = useState([]);
  const [carouselMode, setCarouselMode] = useState(false);
  // File selezionati nel campo singolo e passati al carousel (hand-off via stato,
  // non via globale: evita di smontare l'uploader durante il caricamento)
  const [pendingFiles, setPendingFiles] = useState(null);
  const [carouselBusy, setCarouselBusy] = useState(false);
  // Auto-revert to single-media mode when carousel is emptied by the user
  useEffect(() => {
    if (carouselMode && carousel.length === 0 && !pendingFiles && !carouselBusy) {
      setCarouselMode(false);
    }
  }, [carouselMode, carousel.length, pendingFiles, carouselBusy]);
  const [igMediaType, setIgMediaType] = useState("IMAGE"); // for single-item IG (REELS/STORIES)
  const [scheduledAt, setScheduledAt] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // {id, name}
  // Location tag (works for both FB place & IG location_id — same Page ID)
  const [location, setLocation] = useState(null);     // {id, name, address}
  const [locationEnabled, setLocationEnabled] = useState(false);
  useEffect(() => {
    api.get("/meta/pages/config")
      .then((r) => setLocationEnabled(!!r.data?.location_tag_enabled))
      .catch(() => setLocationEnabled(false));
  }, []);
  // IG user tags: [{username, x, y}]
  const [userTags, setUserTags] = useState([]);

  // Meta connection profile for live preview
  const [metaProfile, setMetaProfile] = useState({ facebook: null, instagram: null });
  const [refreshingMeta, setRefreshingMeta] = useState(false);
  const loadMetaProfile = useCallback(async () => {
    try {
      const r = await api.get("/meta/pages/config");
      setMetaProfile({ facebook: r.data.facebook, instagram: r.data.instagram });
    } catch (e) { /* not connected — fine, preview shows fallback */ }
  }, []);
  useEffect(() => { loadMetaProfile(); }, [loadMetaProfile]);
  const refreshMeta = async () => {
    setRefreshingMeta(true);
    await loadMetaProfile();
    setRefreshingMeta(false);
    toast.info(t("publish.profilesRefreshed"));
  };

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get("/meta/posts?status=published");
      setHistory(r.data.data || []);
    } catch (e) { /* noop */ }
  }, []);
  const loadDrafts = useCallback(async () => {
    try {
      const r1 = await api.get("/meta/posts?status=draft");
      const r2 = await api.get("/meta/posts?status=scheduled");
      setDrafts([...(r1.data.data || []), ...(r2.data.data || [])]);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { loadHistory(); loadDrafts(); }, [loadHistory, loadDrafts]);

  const resetForm = () => {
    setText(""); setSingleMedia(null); setCarousel([]);
    setCarouselMode(false); setScheduledAt(""); setEditingId(null);
    setLocation(null); setUserTags([]);
  };

  // Open Compose with a specific date pre-filled (default 10:00) — called from Calendar
  const composeForDate = (iso) => {
    resetForm();
    const pad = (n) => String(n).padStart(2, "0");
    const now = new Date();
    const target = new Date(iso + "T10:00");
    // If chosen day == today and 10:00 is past, bump to now+15min
    if (target < new Date(now.getTime() + 10 * 60 * 1000)) {
      const bump = new Date(now.getTime() + 15 * 60 * 1000);
      setScheduledAt(`${bump.getFullYear()}-${pad(bump.getMonth() + 1)}-${pad(bump.getDate())}T${pad(bump.getHours())}:${pad(bump.getMinutes())}`);
    } else {
      setScheduledAt(`${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T10:00`);
    }
    setTab("compose");
    toast.info(t("publish.newPostFor", { date: fmtDate(iso, { weekday: "long", day: "numeric", month: "long" }) }));
  };

  const loadIntoForm = (p) => {
    setEditingId(p.id);
    setText(p.message || p.caption || "");
    setPlatforms({
      facebook: (p.platforms || [p.platform]).includes("facebook"),
      instagram: (p.platforms || [p.platform]).includes("instagram"),
    });
    if (p.media_urls?.length >= 2) {
      setCarouselMode(true);
      setCarousel(p.media_urls.map((url) => ({ url, kind: url.match(/\.(mp4|mov|webm)$/i) ? "video" : "image" })));
      setSingleMedia(null);
    } else if (p.image_url || p.video_url) {
      setCarouselMode(false);
      setSingleMedia({ url: p.image_url || p.video_url, kind: p.image_url ? "image" : "video" });
      setCarousel([]);
    }
    setIgMediaType(p.ig_media_type || "IMAGE");
    setLocation(p.location_id || p.place
      ? { id: p.location_id || p.place, name: p.location_name || t("publish.savedPlace"), address: null }
      : null);
    setUserTags(Array.isArray(p.user_tags) ? p.user_tags : []);
    if (p.scheduled_at) {
      const d = new Date(p.scheduled_at);
      const pad = (n) => String(n).padStart(2, "0");
      setScheduledAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    } else {
      setScheduledAt("");
    }
    setTab("compose");
  };

  const publish = async () => {
    const selected = Object.entries(platforms).filter(([_, v]) => v).map(([k]) => k);
    if (selected.length === 0) {
      toast.error(t("publish.pickPlatform"));
      return;
    }
    setPublishing(true);
    const errors = [];
    const successes = [];

    const singleImageUrl = singleMedia?.kind === "image" ? singleMedia.url : undefined;
    const singleVideoUrl = singleMedia?.kind === "video" ? singleMedia.url : undefined;
    const mediaUrls = carouselMode && carousel.length >= 2 ? carousel.map(c => c.url) : undefined;

    // Se sto modificando una bozza/programmata, elimino il record originale
    // (verrà sostituito dai nuovi record di publish/*).
    if (editingId) {
      try { await api.delete(`/meta/posts/${editingId}`); } catch { /* ignore */ }
    }

    const cleanUserTags = userTags
      .filter((u) => (u.username || "").trim())
      .map((u) => ({ username: u.username.replace(/^@/, "").trim(), x: u.x ?? 0.5, y: u.y ?? 0.5 }));

    for (const p of selected) {
      try {
        if (p === "facebook") {
          await api.post("/meta/publish/facebook", {
            message: text || undefined,
            image_url: !carouselMode ? singleImageUrl : undefined,
            video_url: !carouselMode ? singleVideoUrl : undefined,
            media_urls: mediaUrls,
            scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
            place: location?.id || undefined,
          });
          successes.push("Facebook");
        } else if (p === "instagram") {
          // Se c'è scheduled_at → salviamo come 'scheduled' via draft-endpoint,
          // il worker si occuperà di pubblicare al momento.
          if (scheduledAt) {
            await api.post("/meta/publish/draft", {
              platforms: ["instagram"],
              text: text || undefined,
              image_url: singleImageUrl,
              video_url: singleVideoUrl,
              media_urls: mediaUrls,
              ig_media_type: carouselMode ? "CAROUSEL" : igMediaType,
              scheduled_at: new Date(scheduledAt).toISOString(),
              location_id: location?.id || undefined,
              location_name: location?.name || undefined,
              user_tags: cleanUserTags.length ? cleanUserTags : undefined,
            });
            successes.push(t("publish.igScheduledLabel"));
          } else {
            const igMt = carouselMode ? "CAROUSEL" : igMediaType;
            await api.post("/meta/publish/instagram", {
              caption: text || undefined,
              image_url: !carouselMode ? singleImageUrl : undefined,
              video_url: !carouselMode ? singleVideoUrl : undefined,
              media_type: igMt,
              media_urls: mediaUrls,
              location_id: location?.id || undefined,
              user_tags: (cleanUserTags.length && (igMt === "IMAGE")) ? cleanUserTags : undefined,
            });
            successes.push("Instagram");
          }
        }
      } catch (e) {
        errors.push(`${p}: ${e?.response?.data?.detail || t("common.error")}`);
      }
    }

    if (successes.length) toast.success(scheduledAt
      ? t("publish.scheduledOn", { list: successes.join(" + ") })
      : t("publish.publishedOn", { list: successes.join(" + ") }));
    if (errors.length) errors.forEach(e => toast.error(e));
    if (successes.length && !errors.length) resetForm();
    loadHistory(); loadDrafts();
    setPublishing(false);
  };

  const saveDraft = async () => {
    const selected = Object.entries(platforms).filter(([_, v]) => v).map(([k]) => k);
    if (selected.length === 0) {
      toast.error(t("publish.pickPlatform"));
      return;
    }
    setSavingDraft(true);
    try {
      const singleImageUrl = singleMedia?.kind === "image" ? singleMedia.url : undefined;
      const singleVideoUrl = singleMedia?.kind === "video" ? singleMedia.url : undefined;
      const mediaUrls = carouselMode && carousel.length >= 2 ? carousel.map(c => c.url) : undefined;
      const cleanUserTags = userTags
        .filter((u) => (u.username || "").trim())
        .map((u) => ({ username: u.username.replace(/^@/, "").trim(), x: u.x ?? 0.5, y: u.y ?? 0.5 }));
      const payload = {
        platforms: selected,
        text: text || undefined,
        image_url: singleImageUrl,
        video_url: singleVideoUrl,
        media_urls: mediaUrls,
        ig_media_type: carouselMode ? "CAROUSEL" : igMediaType,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        place: location?.id || undefined,
        location_id: location?.id || undefined,
        location_name: location?.name || undefined,
        user_tags: cleanUserTags.length ? cleanUserTags : undefined,
      };
      if (editingId) {
        await api.patch(`/meta/posts/${editingId}`, payload);
        toast.success(t("publish.draftUpdated"));
      } else {
        await api.post("/meta/publish/draft", payload);
        toast.success(scheduledAt ? t("publish.postScheduled") : t("publish.draftSaved"));
      }
      resetForm();
      loadDrafts();
      setTab("drafts");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("publish.saveError"));
    }
    setSavingDraft(false);
  };

  const doDeletePost = async () => {
    if (!confirmDel) return;
    try {
      if (confirmDel.published && confirmDel.isIG) {
        toast.info(t("publish.igDeleteInfo"));
        setConfirmDel(null);
        return;
      }
      const url = confirmDel.published
        ? `/meta/posts/${confirmDel.id}?also_on_meta=true`
        : `/meta/posts/${confirmDel.id}`;
      await api.delete(url);
      toast.success(confirmDel.published ? t("publish.postDeletedFb") : t("publish.postDeleted"));
      setConfirmDel(null);
      loadDrafts(); loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const reuse = (p) => {
    // Copia contenuto in Componi senza collegarlo (crea nuovo post)
    setEditingId(null);
    setText(p.message || p.caption || "");
    setPlatforms({
      facebook: (p.platforms || [p.platform]).includes("facebook"),
      instagram: (p.platforms || [p.platform]).includes("instagram"),
    });
    setSingleMedia(null); setCarousel([]); setCarouselMode(false);
    setScheduledAt("");
    setTab("compose");
    toast.info(t("publish.contentCopied"));
  };

  const hasMedia = carouselMode ? carousel.length >= 2 : !!singleMedia;
  const canPublish = (text || hasMedia) && (platforms.facebook || platforms.instagram);

  return (
    <div data-testid="publish-page" className="space-y-6">
      <header>
        <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight inline-flex items-center gap-3">
          <Share2 className="h-7 w-7 text-[color:var(--sp-brand)]" />
          {t("publish.title")}
        </h1>
        <p className="mt-1 text-sm text-[color:var(--sp-text-secondary)]">
          {t("publish.subtitle")}
        </p>
      </header>

      {/* Platform multi-select */}
      <div className="flex flex-wrap items-center gap-2" data-testid="publish-platform-selector">
        <PlatformCheck
          active={platforms.facebook}
          onClick={() => setPlatforms(p => ({ ...p, facebook: !p.facebook }))}
          icon={Facebook} label="Facebook" color="text-blue-600"
          subtitle={metaProfile.facebook?.page_name || t("publish.notConnected")}
          tid="publish-platform-fb" />
        <PlatformCheck
          active={platforms.instagram}
          onClick={() => setPlatforms(p => ({ ...p, instagram: !p.instagram }))}
          icon={Instagram} label="Instagram" color="text-pink-600"
          subtitle={metaProfile.instagram?.username ? `@${metaProfile.instagram.username}` : t("publish.notConnected")}
          tid="publish-platform-ig" />
        <button type="button" onClick={refreshMeta} disabled={refreshingMeta}
          data-testid="publish-refresh-meta"
          title={t("publish.refreshProfileTooltip")}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[color:var(--sp-border)] bg-white text-[color:var(--sp-text-secondary)] text-xs font-bold hover:bg-[color:var(--sp-subtle)] disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${refreshingMeta ? "animate-spin" : ""}`} />
          {t("publish.refreshProfile")}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[color:var(--sp-border)]">
        {[
          ["compose", t("publish.tabs.compose")],
          ["calendar", t("publish.tabs.calendar")],
          ["drafts", `${t("publish.tabs.drafts")}${drafts.length > 0 ? ` (${drafts.length})` : ""}`],
          ["history", t("publish.tabs.history")],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            data-testid={`publish-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-[color:var(--sp-brand)] text-[color:var(--sp-brand)]" : "border-transparent text-[color:var(--sp-text-secondary)]"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "compose" && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-6">
          {/* LEFT — form */}
          <div className="sp-card p-6 space-y-4" data-testid="publish-compose">
            <Field label={t("publish.text")} icon={FileText}>
              <div className="relative">
                <textarea rows={5} value={text}
                  data-testid="publish-text"
                  onChange={(e) => setText(e.target.value)}
                  className="sp-input pr-28" placeholder={
                    platforms.facebook && platforms.instagram
                      ? t("publish.textBoth")
                      : platforms.instagram ? t("publish.textIg") : t("publish.textFb")} />
                <ComposeToolbar
                  onInsertEmoji={(emoji) => {
                    const ta = document.querySelector('[data-testid="publish-text"]');
                    if (ta && document.activeElement === ta) {
                      const start = ta.selectionStart ?? text.length;
                      const end = ta.selectionEnd ?? text.length;
                      const next = text.slice(0, start) + emoji + text.slice(end);
                      setText(next);
                      requestAnimationFrame(() => {
                        ta.focus();
                        const pos = start + emoji.length;
                        ta.setSelectionRange(pos, pos);
                      });
                    } else {
                      setText(text + emoji);
                    }
                  }}
                  location={location} setLocation={setLocation}
                  locationEnabled={locationEnabled}
                  userTags={userTags} setUserTags={setUserTags}
                  showTagButton
                  tagsHint={
                    !platforms.instagram
                      ? t("publish.tagsHintNoIg")
                      : !(platforms.instagram && !carouselMode && igMediaType === "IMAGE")
                      ? t("publish.tagsHintImageOnly")
                      : null
                  }
                />
                {/* Selected chips */}
                {(location || (userTags && userTags.filter(u => u.username).length > 0)) && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="publish-compose-chips">
                    {locationEnabled && location && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[color:var(--sp-subtle)] text-[11px] font-bold"
                            data-testid="publish-chip-location">
                        <MapPin className="h-3 w-3 text-[color:var(--sp-brand)]" /> {location.name}
                        <button type="button" onClick={() => setLocation(null)}
                          className="ml-0.5 hover:text-red-600"><X className="h-3 w-3" /></button>
                      </span>
                    )}
                    {(userTags || []).filter(u => u.username).map((u, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-pink-50 text-pink-700 text-[11px] font-bold"
                            data-testid={`publish-chip-tag-${i}`}>
                        <AtSign className="h-3 w-3" /> {u.username}
                        <button type="button" onClick={() => setUserTags(userTags.filter((_, idx) => idx !== i))}
                          className="ml-0.5 hover:text-red-600"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-1 flex items-center justify-between text-[10px] text-[color:var(--sp-text-muted)]">
                  <span>{t("publish.chars", { n: text.length })}</span>
                  {platforms.instagram && text.length > 2200 && <span className="text-red-600 font-bold">{t("publish.igLimit")}</span>}
                </div>
              </div>
            </Field>

            {platforms.instagram && !carouselMode && (
              <Field label={t("publish.igType")}>
                <div className="grid grid-cols-4 gap-2" data-testid="publish-ig-mediatype">
                  {["IMAGE", "VIDEO", "REELS", "STORIES"].map((t) => (
                    <button key={t} type="button"
                      data-testid={`publish-ig-mediatype-${t.toLowerCase()}`}
                      onClick={() => setIgMediaType(t)}
                      className={`px-2 py-2 rounded-lg text-[11px] font-bold border transition-colors ${igMediaType === t ? "border-[color:var(--sp-brand)] bg-[color:var(--sp-brand)] text-white" : "border-[color:var(--sp-border)] text-[color:var(--sp-text-secondary)]"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </Field>
            )}

            {carouselMode ? (
              <MultiMediaUploader
                value={carousel}
                onChange={setCarousel}
                pendingFiles={pendingFiles}
                onPendingConsumed={() => setPendingFiles(null)}
                onUploadingChange={setCarouselBusy}
              />
            ) : (
              <SingleMediaField
                value={singleMedia}
                onChange={setSingleMedia}
                onMultiSelected={(files) => {
                  // Utente ha selezionato ≥2 file in un colpo solo → passa automaticamente al carousel
                  setSingleMedia(null);
                  setPendingFiles(files);
                  setCarouselBusy(true);
                  setCarouselMode(true);
                  toast.info(t("publish.carouselAuto"));
                }}
              />
            )}

            {/* Instagram Music info — feature not exposed by Meta API */}
            {platforms.instagram && (igMediaType === "REELS" || igMediaType === "STORIES" || carouselMode) && (
              <MusicNote />
            )}

            {platforms.facebook && (
              <Field label={t("publish.schedule")} icon={Calendar}>
                <input type="datetime-local" value={scheduledAt}
                  data-testid="publish-fb-schedule"
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="sp-input" />
                <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">
                  {t("publish.scheduleHint")}
                </div>
              </Field>
            )}
            {!platforms.facebook && platforms.instagram && (
              <Field label={t("publish.scheduleIg")} icon={Calendar}>
                <input type="datetime-local" value={scheduledAt}
                  data-testid="publish-ig-schedule"
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="sp-input" />
                <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">
                  {t("publish.scheduleIgHint")}
                </div>
              </Field>
            )}

            <div className="flex justify-end gap-2 pt-2 flex-wrap">
              {editingId && (
                <button onClick={resetForm}
                  data-testid="publish-cancel-edit-btn"
                  className="sp-btn-ghost text-sm">{t("publish.cancelEdit")}</button>
              )}
              <button onClick={saveDraft} disabled={savingDraft || !canPublish}
                data-testid="publish-save-draft-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[color:var(--sp-border)] text-sm font-bold hover:bg-[color:var(--sp-subtle)] disabled:opacity-50">
                {savingDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {t("publish.saveDraft")}
              </button>
              <button onClick={publish} disabled={publishing || !canPublish}
                data-testid="publish-submit-btn"
                className="sp-btn-primary inline-flex items-center gap-2">
                {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> :
                  (scheduledAt ? <Clock className="h-4 w-4" /> : <Send className="h-4 w-4" />)}
                {scheduledAt ? t("publish.scheduleBtn") : (editingId ? t("publish.publishNow") : t("publish.publishBtn"))}
              </button>
            </div>
          </div>

          {/* RIGHT — Live preview */}
          <div className="space-y-4 lg:sticky lg:top-4 self-start" data-testid="publish-preview-panel">
            {!platforms.facebook && !platforms.instagram && (
              <EmptyPreview />
            )}
            {platforms.facebook && (
              <FbPreview
                profile={metaProfile.facebook}
                text={text}
                media={carouselMode ? carousel : (singleMedia ? [singleMedia] : [])}
                scheduledAt={scheduledAt}
              />
            )}
            {platforms.instagram && (
              <IgPreview
                profile={metaProfile.instagram}
                caption={text}
                media={carouselMode ? carousel : (singleMedia ? [singleMedia] : [])}
                mediaType={carouselMode ? "CAROUSEL" : igMediaType}
              />
            )}
          </div>
        </div>
      )}

      {tab === "calendar" && (
        <CalendarView
          onEdit={loadIntoForm}
          onDelete={(p) => setConfirmDel({
            id: p.id,
            name: p.message || p.caption || t("publish.noText"),
            published: p._published || p.status === "published",
            isIG: p._isIG || p.platform === "instagram" || (p.platforms || []).includes("instagram"),
          })}
          onNewForDate={composeForDate}
        />
      )}

      {tab === "drafts" && (
        <DraftsList items={drafts} onEdit={loadIntoForm}
          onDelete={(p) => setConfirmDel({ id: p.id, name: p.message || p.caption || t("publish.noText") })} />
      )}

      {tab === "history" && (
        <div className="sp-card p-6" data-testid="publish-history">
          {history.length === 0 ? (
            <div className="text-center py-8 text-sm text-[color:var(--sp-text-muted)]">
              {t("publish.noPosts")}
            </div>
          ) : (
            <ul className="space-y-3">
              {history.map((p) => {
                const isIG = p.platform === "instagram" || (p.platforms || []).includes("instagram");
                return (
                <li key={p.id} className="border-b border-[color:var(--sp-border)] pb-3 last:border-0">
                  <div className="flex items-start gap-3">
                    {isIG ? <Instagram className="h-4 w-4 text-pink-600 mt-1" /> : <Facebook className="h-4 w-4 text-blue-600 mt-1" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">{p.message || p.caption || t("publish.mediaOnly")}</div>
                      <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1 flex items-center gap-2 flex-wrap">
                        <span>{fmtDateTime(p.created_at)}</span>
                        {p.media_urls?.length >= 2 && (
                          <span className="inline-flex items-center gap-0.5 text-purple-600">
                            <Images className="h-3 w-3" /> {t("publish.carousel")} · {p.media_urls.length}
                          </span>
                        )}
                        {p.scheduled_at && <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" /> {fmtDateTime(p.scheduled_at)}</span>}
                        <StatusPill status={p.status} />
                      </div>
                      {p.permalink_url && (
                        <a href={p.permalink_url} target="_blank" rel="noopener noreferrer"
                          data-testid={`publish-history-open-${p.id}`}
                          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-[color:var(--sp-brand)] hover:underline">
                          {t("publish.openOn", { platform: isIG ? "Instagram" : "Facebook" })}
                        </a>
                      )}
                    </div>
                    <button onClick={() => reuse(p)}
                      data-testid={`publish-reuse-${p.id}`}
                      className="p-2 rounded-lg text-[color:var(--sp-text-muted)] hover:bg-[color:var(--sp-subtle)] hover:text-[color:var(--sp-brand)]"
                      title={t("publish.reuseTooltip")}>
                      <Copy className="h-4 w-4" />
                    </button>
                    <button onClick={() => setConfirmDel({ id: p.id, name: p.message || p.caption || t("publish.noText"), published: true, isIG })}
                      data-testid={`publish-history-delete-${p.id}`}
                      className="p-2 rounded-lg text-[color:var(--sp-text-muted)] hover:bg-[color:var(--sp-danger)]/10 hover:text-[color:var(--sp-danger)]"
                      title={isIG ? t("publish.deleteIgTooltip") : t("publish.deleteSocialTooltip")}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setConfirmDel(null)} data-testid="publish-confirm-dialog">
          <div className="sp-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <span className="h-10 w-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-lg font-black">
                  {confirmDel.published
                    ? (confirmDel.isIG ? t("publish.del.igTitle") : t("publish.del.fbTitle"))
                    : t("publish.del.title")}
                </h3>
                <p className="text-sm text-[color:var(--sp-text-secondary)] mt-1">
                  {confirmDel.published && confirmDel.isIG ? (
                    <Trans i18nKey="publish.del.igBody" components={{ 1: <b /> }} />
                  ) : confirmDel.published ? (
                    <Trans i18nKey="publish.del.fbBody" values={{ name: confirmDel.name.slice(0, 60) }} components={{ 1: <b /> }} />
                  ) : (
                    t("publish.del.body", { name: confirmDel.name.slice(0, 80) })
                  )}
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)}
                className="px-4 py-2 rounded-xl border border-[color:var(--sp-border)] text-sm font-bold hover:bg-[color:var(--sp-subtle)]">
                {confirmDel.published && confirmDel.isIG ? t("publish.del.understood") : t("common.cancel")}
              </button>
              {!(confirmDel.published && confirmDel.isIG) && (
                <button onClick={doDeletePost}
                  data-testid="publish-confirm-delete"
                  className="px-4 py-2 rounded-xl bg-[color:var(--sp-danger)] text-white text-sm font-bold hover:opacity-90">
                  {confirmDel.published ? t("publish.del.deleteFb") : t("common.delete")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// Small UI atoms
// ==========================================================================
